import { ReactNode } from 'react';
import { HelpIconTooltip } from './HelpIconTooltip';
import { useCurrency } from '@/hooks/useCurrency';

type KpiVariant = 'default' | 'danger' | 'warning';

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  /** Texto junto al valor principal (ej. unidad «meses»). */
  valueSuffix?: string;
  /** Segunda cifra bajo el valor principal (ej. importe en €). */
  secondaryValue?: string | number | null;
  secondaryIsEuro?: boolean;
  /** Clases del secondaryValue (p.ej. color de sesgo firmado). */
  secondaryClassName?: string;
  /** Nota breve bajo el valor (ej. «en unidades»). */
  valueNote?: string;
  /** Clases del pie bajo el valor (por defecto `text-sm text-gray-500 mt-1`). */
  subtitleClassName?: string;
  icon?: ReactNode;
  trend?: number;
  trendLabel?: string;
  isPercentage?: boolean;
  isEuro?: boolean;
  isCount?: boolean;
  onClick?: () => void;
  loading?: boolean;
  /** Solo colorea borde de tarjeta y el número principal; título, icono y pie siguen neutros. */
  variant?: KpiVariant;
  /** Texto de ayuda mostrado al pasar el cursor sobre el icono ℹ️ junto al título. */
  helpText?: string;
  /** Indicador secundario compacto dentro de la card (p.ej. sesgo usuario bajo adherencia). */
  nested?: {
    title: string;
    value: string;
    helpText?: string;
    subtitle?: string;
    valueClassName?: string;
  } | null;
}

/** Estilo neutro compartido (título, icono, sufijo). */
const neutral = {
  title: 'text-gray-900',
  suffix: 'text-gray-600',
  iconWrap: 'text-[#1681ba]',
} as const;

/** Solo borde de la tarjeta + color del valor numérico principal. */
const variantStyles: Record<KpiVariant, { card: string; value: string }> = {
  default: {
    card: 'border-gray-200 bg-white',
    value: 'text-gray-900',
  },
  danger: {
    card: 'border-2 border-red-400 bg-white',
    value: 'text-red-600',
  },
  warning: {
    card: 'border-2 border-orange-500 bg-white',
    value: 'text-orange-600',
  },
};

export function KpiCard({
  title,
  value,
  subtitle,
  valueSuffix,
  secondaryValue,
  secondaryIsEuro = false,
  secondaryClassName,
  valueNote,
  subtitleClassName,
  icon,
  trend,
  trendLabel,
  isPercentage = false,
  isEuro = false,
  isCount = false,
  onClick,
  loading = false,
  variant = 'default',
  helpText,
  nested,
}: KpiCardProps) {
  const vs = variantStyles[variant] ?? variantStyles.default;
  const { formatRounded } = useCurrency();

  if (loading) {
    return (
      <div
        className={`rounded-lg shadow-sm p-6 border flex flex-col items-center justify-center text-center h-full animate-pulse ${vs.card}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="h-8 bg-gray-300 rounded w-32 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-16"></div>
      </div>
    );
  }

  const formatValue = () => {
    // Si el valor es un string (como "-"), retornarlo directamente
    if (typeof value === 'string') {
      return value;
    }

    if (isEuro) {
      return formatRounded(value);
    }
    if (isPercentage) {
      return `${value}%`;
    }
    if (isCount) {
      return Number(value).toLocaleString('es-ES');
    }
    return value;
  };

  const getTrendColor = () => {
    if (trend === undefined) return '';
    return trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-500';
  };

  const getTrendIcon = () => {
    if (trend === undefined) return null;
    return trend > 0 ? '↑' : trend < 0 ? '↓' : '→';
  };

  return (
    <div
      className={`rounded-lg shadow-sm p-6 border flex flex-col items-center justify-center text-center h-full ${vs.card} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="mb-3 flex items-center justify-center gap-2">
        {icon && <div className={neutral.iconWrap}>{icon}</div>}
        <h3 className={`text-base font-semibold ${neutral.title}`}>{title}</h3>
        {helpText ? <HelpIconTooltip text={helpText} /> : null}
      </div>

      <div className="flex flex-col items-center gap-0.5">
        <div className="flex flex-wrap items-baseline justify-center gap-x-1.5 gap-y-0">
          <span className={`text-3xl font-bold tabular-nums tracking-tight ${vs.value}`}>{formatValue()}</span>
          {valueSuffix ? (
            <span className={`text-xl font-semibold tabular-nums ${neutral.suffix}`}>{valueSuffix}</span>
          ) : null}
        </div>
        {secondaryValue != null && secondaryValue !== '' ? (
          <span
            className={
              secondaryClassName ??
              'text-lg font-semibold tabular-nums text-gray-600'
            }
          >
            {secondaryIsEuro
              ? formatRounded(secondaryValue)
              : secondaryValue}
          </span>
        ) : null}
      </div>

      {valueNote ? <p className="mt-1 text-xs text-gray-500">{valueNote}</p> : null}

      {subtitle && (
        <p className={subtitleClassName ?? 'mt-1.5 text-sm text-gray-500'}>{subtitle}</p>
      )}

      {nested ? (
        <div className="mt-3 w-full border-t border-gray-100 pt-2">
          <div className="flex items-center justify-center gap-1 text-xs font-medium text-gray-600">
            <span>{nested.title}</span>
            {nested.helpText ? <HelpIconTooltip text={nested.helpText} /> : null}
          </div>
          <p
            className={`mt-0.5 text-sm font-semibold tabular-nums ${
              nested.valueClassName ?? 'text-gray-800'
            }`}
          >
            {nested.value}
          </p>
          {nested.subtitle ? (
            <p className="mt-0.5 text-[11px] text-gray-500">{nested.subtitle}</p>
          ) : null}
        </div>
      ) : null}

      {trend !== undefined && (
        <div className={`mt-2 flex items-center justify-center ${getTrendColor()}`}>
          <span className="mr-1 text-sm">{getTrendIcon()}</span>
          <span className="text-sm font-medium">
            {Math.abs(trend)}% {trendLabel || ''}
          </span>
        </div>
      )}
    </div>
  );
}
